import { getCurrentScope, onScopeDispose, ref } from "vue";
import { logger } from "@/infrastructure/services/LoggerService";
/** How long the "copied" acknowledgement stays on screen, matching the mail-editor tooltip. */
const ACKNOWLEDGEMENT_MS = 2000;
/**
 * Copies text and exposes which item was copied last, for a transient "copied" acknowledgement.
 *
 * `copiedId` rather than a boolean, so a panel with several copy buttons can tell which one was
 * pressed — a shared boolean lights every button at once, which reads as "everything was copied".
 *
 * The clipboard API rejects when the document is not focused or the permission is denied. There is
 * no user-facing recovery beyond selecting the text by hand, so the failure is logged (R47) and
 * the acknowledgement simply does not appear.
 */
export function useCopyToClipboard() {
    const copiedId = ref(null);
    let timer;
    async function copy(text, id) {
        try {
            await navigator.clipboard.writeText(text);
        }
        catch (err) {
            logger.error("Failed to copy to clipboard", err);
            return false;
        }
        copiedId.value = id;
        // Cleared and re-armed, so copying a second value does not inherit the first one's deadline
        // and blink out early.
        clearTimeout(timer);
        timer = setTimeout(() => {
            copiedId.value = null;
        }, ACKNOWLEDGEMENT_MS);
        return true;
    }
    // Scope-level rather than `onUnmounted`: the cleanup belongs to whatever effect scope owns the
    // composable, and `onUnmounted` warns (and silently does nothing) when there is no component —
    // which is exactly the case in a unit test, leaving a live timer behind after each one.
    if (getCurrentScope()) {
        onScopeDispose(() => clearTimeout(timer));
    }
    return { copiedId, copy };
}
